import {Box, HStack, Image, Space, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNotEmpty, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, useNavigate, useOnMount} from 'shared/web';

import {LanguageSwitcher} from '../../components/Layout/components/Header/components/LanguageSwitcher';
import {PageBackgroundGradient} from '../../components/PageBackgroundGradient/PageBackgroundGradient';
import i18n from '../../i18n/i18n';
import {useCreateBatchMutation} from '../../store/importApi';
import {handleApiError} from '../../utils/handleApiError';
import {getImportBatchPath, getImportPath} from '../../utils/routing';
import {Stepper} from '../Create/components/Stepper';
import {ImportResultView} from './components/ImportResultView';
import {ImportFormPayload, ImportUploadForm} from './components/ImportUploadForm';

const IMPORT_STEP_UPLOAD = 0;
const IMPORT_STEP_RESULT = 1;

export function ImportPage(props: TestIdProps) {
  const params = useParams<{batchId?: string}>();
  const navigate = useNavigate();
  const [createBatch, {isLoading: isCreateBatchLoading}] = useCreateBatchMutation();
  const {batchId} = params;

  const importSteps = [
    i18n.t('page.import.labels.stepFileUpload'),
    i18n.t('page.import.labels.stepDataValidation'),
    i18n.t('page.import.labels.stepVehiclePricing'),
  ];

  const activeStep = isNotNil(batchId) ? IMPORT_STEP_RESULT : IMPORT_STEP_UPLOAD;

  useOnMount(() => {
    document.getElementById('loading-screen')?.remove();
  });

  const handleStepChange = (step: number) => {
    if (step === IMPORT_STEP_UPLOAD) {
      navigate(getImportPath());
      return;
    }
    if (step === IMPORT_STEP_RESULT && isNotNil(batchId)) {
      navigate(getImportBatchPath(batchId));
    }
  };

  const handleImport = (payload: ImportFormPayload) => {
    createBatch({
      body: {
        file: payload.file,
        market: payload.market,
        ...(isNotNil(payload.name) && isNotEmpty(payload.name) && {name: payload.name}),
        use_vin_decoding: payload.use_vin_decoding ?? false,
        lang: i18n.language,
      },
    })
      .unwrap()
      .then((response) => {
        navigate(getImportBatchPath(response.id));
      })
      .catch(handleApiError);
  };

  return (
    <>
      <Box backgroundColor="palettes.blue.70.100" paddingHorizontal={6}>
        <HStack height={12} align="center" justify="space-between">
          <Image src="assets/images/logo.svg" maxWidth={125} alt="Logo" />
          <LanguageSwitcher data-testid={suffixTestId('languageSwitcher', props)} />
        </HStack>
      </Box>

      <PageBackgroundGradient data-testid={suffixTestId('background', props)}>
        <VStack shrink={1} maxWidth={315} width="100%" align="center">
          <Stepper
            steps={importSteps}
            value={activeStep}
            onChange={handleStepChange}
            data-testid={suffixTestId('stepper', props)}
          />
          <Space vertical={6} />
          {activeStep === IMPORT_STEP_RESULT && isNotNil(batchId) ? (
            <ImportResultView batchId={batchId} data-testid={suffixTestId('result', props)} />
          ) : (
            <ImportUploadForm
              onImport={handleImport}
              isImporting={isCreateBatchLoading}
              data-testid={suffixTestId('uploadForm', props)}
            />
          )}
        </VStack>
      </PageBackgroundGradient>
    </>
  );
}
